import useTaskPlanning from "./useTaskPlanning";
import { AddEditDependency } from "./Dependency";
import { FormProvider } from "react-hook-form";
import { Button } from "@/components/ui";
import { DataTable, Dialog, FormCheckbox, FormDatePicker, FormInput, FormSelect, Loader, TaskFormContainer } from "@/components";
import { ID, CATEGORY, SUB_CATEGORY, TASK_NAME, DAYS, PLANNED_START_DATE, PLANNED_END_DATE, SAFETY_CHECK, QUALITY_CHECK } from "./consts";
import { CircleAlert } from "lucide-react";

const TaskPlanning = () => {
  const {
    taskData,
    form,
    onSubmit,
    requiredFields,
    isLoadingTask,
    isCategoryListLoading,
    categoryList,
    subCategoryList,
    isSubCategoryListLoading,
    isTaskRefetching,
    planId,
    isView,
    isAddTaskPending,
    isUpdateTaskPending,
    isLoadingDependency,
    onAddDeps,
    openAddEditDepsDialog,
    setOpenAddEditDepsDialog,
    isEditDeps,
    depsList,
    onClear,
    onSaveDeps,
    isRefetchingSubCategories,
    depsColumns,
    openDeleteDepsDialog,
    setOpenDeleteDepsDialog,
    onConfirmDeleteDeps,
    isDeletePending,
    dependencyData,
    isTaskExist
  } = useTaskPlanning();
  const {errors} = form.formState;
  
  return (
    <div className="py-3">
      {isLoadingTask || isCategoryListLoading || isTaskRefetching ? <Loader /> : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-[20px] min-h-[400px] overflow-y-scroll px-5 pb-10 no-scrollbar">
              {taskData && planId && 
                <TaskFormContainer label="ID" isRequired>
                  <FormInput
                    name={ID}
                    control={form.control}
                    label=""
                    placeholder="Please enter"
                    error={errors[ID]?.message}
                    isRequired={requiredFields.includes(ID)}
                    disabled
                    maxLength={50}
                  />
                </TaskFormContainer>
              }
              <TaskFormContainer label="Category" isRequired>
                <FormSelect
                  name={CATEGORY}
                  control={form.control}
                  label=""
                  placeholder="Please select"
                  error={errors[CATEGORY]?.message}
                  isRequired={requiredFields.includes(CATEGORY)}
                  options={categoryList?.data?.map((data: any) => ({label: data?.title, value: data?.taskCategoryId?.toString()}))}
                  disabled={isView}
                />
              </TaskFormContainer>
              <TaskFormContainer label="Sub Category" isRequired>
                <FormSelect
                  name={SUB_CATEGORY}
                  control={form.control}
                  label=""
                  placeholder="Please select"
                  error={errors[SUB_CATEGORY]?.message}
                  isRequired={requiredFields.includes(SUB_CATEGORY)}
                  options={subCategoryList?.data?.map((data: any) => ({label: data?.title, value: data?.taskSubCategoryId?.toString()}))}
                  disabled={!form.watch(CATEGORY) || isView || isRefetchingSubCategories || isSubCategoryListLoading}
                />
              </TaskFormContainer>
              <TaskFormContainer label="Task Name" isRequired>
                <FormInput
                  name={TASK_NAME}
                  control={form.control}
                  label=""
                  placeholder="Please enter"
                  error={errors[TASK_NAME]?.message}
                  isRequired={requiredFields.includes(TASK_NAME)}
                  maxLength={50}
                  disabled={isView}
                />
              </TaskFormContainer>
              <TaskFormContainer label="Duration (Days)" isRequired>
                <FormInput
                  name={DAYS}
                  control={form.control}
                  label=""
                  placeholder="Please enter"
                  error={errors[DAYS]?.message}
                  isRequired={requiredFields.includes(DAYS)}
                  disabled={isView}
                />
              </TaskFormContainer>
              <TaskFormContainer label="Planned Start Date">
                <FormDatePicker
                  name={PLANNED_START_DATE}
                  control={form.control}
                  label=""
                  placeholder="Please select"
                  error={errors[PLANNED_START_DATE]?.message}
                  isRequired={requiredFields.includes(PLANNED_START_DATE)}
                  // onChange={(date: any) =>
                  //   setDuration(date, form.watch(PLANNED_END_DATE))
                  // }
                  disabled={isView}
                />
                {!isTaskExist && !form.watch(PLANNED_START_DATE) ? 
                  <div className="bg-red-600 rounded-sm flex flex-row items-center text-white gap-2 mt-2 px-2 py-1">
                    <div>
                      <CircleAlert size={18} />
                    </div>
                    <div className="text-[13px]">
                      Start Date is mandatory for first task
                    </div>
                    {/* <div className="cursor-pointer">
                      <X size={18} />
                    </div> */}
                  </div>
                  : <></>}
              </TaskFormContainer>
              <TaskFormContainer label="Planned End Date">
                <FormDatePicker
                  name={PLANNED_END_DATE}
                  control={form.control}
                  label=""
                  placeholder="Please select"
                  error={errors[PLANNED_END_DATE]?.message}
                  isRequired={requiredFields.includes(PLANNED_END_DATE)}
                  minDate={form.watch(PLANNED_START_DATE)}
                  // onChange={(date: any) =>
                  //   setDuration(form.watch(PLANNED_START_DATE), date)
                  // }
                  disabled={isView}
                />
              </TaskFormContainer>
              {planId ? <div></div> : <></>}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex flex-col gap-5">
                  <FormCheckbox
                    name={SAFETY_CHECK}
                    control={form.control}
                    label="Safety Compliance Check"
                    error={errors[SAFETY_CHECK]?.message}
                    isRequired={requiredFields.includes(SAFETY_CHECK)}
                    disabled={isView}
                  />
                </div>
                <div className="flex flex-col gap-5">
                  <FormCheckbox
                    name={QUALITY_CHECK}
                    control={form.control}
                    label="Quality Control Check"
                    error={errors[QUALITY_CHECK]?.message}
                    isRequired={requiredFields.includes(QUALITY_CHECK)}
                    disabled={isView}
                  />
                </div>
              </div>
            </div>
            <hr />
            <div>
              <div className="flex flex-row items-center justify-between px-5 pb-5 mt-3">
                <h4 className={"font-[600] text-[14px] text-start text-yellow-700"}>
                  Dependency
                </h4>
                {isView ? <></> : <Button variant="link" type="button" className="text-primary underline" onClick={onAddDeps}>Add</Button>}
              </div>
              
              {
                isLoadingDependency ? <Loader /> : 
                  <DataTable
                    data={depsList || []}
                    columns={depsColumns}
                    className="shadow-none border-0"
                  />
              }
            </div>
            <div className="flex justify-end gap-5 px-4">
              <Button type="button" variant="outline" onClick={onClear} disabled={isAddTaskPending || isAddTaskPending}>Clear</Button>
              {isView ? <></> : <Button type="submit" disabled={isUpdateTaskPending || isUpdateTaskPending}>
                {isUpdateTaskPending || isUpdateTaskPending ? <Loader variant="secondary" /> : <></>} Save
              </Button>}
            </div>
          </form>
        </FormProvider>
      )}
      {openAddEditDepsDialog && <AddEditDependency
        open={openAddEditDepsDialog}
        setOpen={setOpenAddEditDepsDialog}
        isEdit={isEditDeps.current}
        onSaveDeps={onSaveDeps}
        depsList={depsList}
      />}
      <Dialog
        open={openDeleteDepsDialog}
        setOpen={setOpenDeleteDepsDialog}
        title={"Delete Dependency"}
        saveText="Delete"
        saveBtnClassName="bg-red-600 hover:bg-red-600 text-white hover:text-white"
        onSave={onConfirmDeleteDeps}
        isLoading={isDeletePending}
      >
        <p>Are you sure you want to delete dependency of the task - <b>{dependencyData?.dependsOnTaskName || ''}</b></p>
      </Dialog>
    </div>
  );
};

export default TaskPlanning;
